import React, { useState, useEffect } from 'react';
import { useToast } from '../../hooks/useToast';
import { format, parseISO } from 'date-fns';
import { Modal, RowSkeleton } from '../ui';
import { useConfirm } from '../../hooks/useConfirm';

export default function HistoryModal({ onClose, onRestore, getSnapshots }) {
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [restoring, setRestoring] = useState(null);

    useEffect(() => {
        getSnapshots().then(snaps => { setSnapshots(snaps); setLoading(false); }).catch(() => setLoading(false));
    }, []);

    // In-app confirm + toast (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    const { show } = useToast();
    const handleRestore = async (date) => {
        if (!(await confirmDialog({
            title: `Restore the board to ${date}?`,
            body: `Every job and availability entry on the board now is replaced with the ${date} snapshot. The current state is saved as today's snapshot first, so it can be restored again.`,
            confirmLabel: `Restore to ${date}`,
            danger: true,
        }))) return;
        setRestoring(date);
        try {
            await onRestore(date);
            onClose();
        } catch (e) { show('Restore failed: ' + (e.message || 'Unknown error'), 'error'); }
        finally { setRestoring(null); }
    };

    return (
        <Modal title="Dispatch Board History" onClose={onClose} width="max-w-lg">
            <div className="space-y-3">
                <p className="text-sm text-slate-600">Daily snapshots are saved automatically. You can restore the board to any previous day's state within the last 7 days.</p>
                {loading && <RowSkeleton rows={3} />}
                {!loading && snapshots.length === 0 && (
                    <div className="text-center py-8 text-slate-400">No snapshots available yet. Snapshots are saved automatically when you make changes to the dispatch board.</div>
                )}
                {!loading && snapshots.map(snap => {
                    const snapDate = snap.snapshot_date;
                    const isToday2 = snapDate === format(new Date(), 'yyyy-MM-dd');
                    const jobCount = (snap.jobs_data || []).length;
                    const availCount = (snap.availability_data || []).length;
                    return (
                        <div key={snap.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                            <div>
                                <div className="font-medium text-sm text-slate-900">
                                    {format(parseISO(snapDate), 'EEEE, MMM d')}
                                    {isToday2 && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Current</span>}
                                </div>
                                <div className="text-xs text-slate-500">{jobCount} jobs, {availCount} availability entries</div>
                            </div>
                            {!isToday2 && (
                                <button
                                    onClick={() => handleRestore(snapDate)}
                                    disabled={restoring === snapDate}
                                    className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {restoring === snapDate ? 'Restoring...' : 'Restore'}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            {confirmElement}
        </Modal>
    );
}
